import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { MatchplayTournament, MatchplayMatch } from '../../types/matchplay-v2';
import { MatchplayDatabase } from '../../lib/matchplay-v2/database';
import { TournamentList } from './tournament/TournamentList';
import { MatchplayTournamentSetup } from './setup/MatchplayTournamentSetup';
import { MatchplayLeaderboard } from './leaderboard/MatchplayLeaderboard';
import { MatchplayScoring } from './scoring/MatchplayScoring';

type DashboardView = 'tournaments' | 'setup' | 'leaderboard' | 'scoring';

interface ViewState {
  view: DashboardView;
  tournamentId?: string;
  matchId?: string;
}

export function MatchplayDashboard() {
  const [viewState, setViewState] = useState<ViewState>({ view: 'tournaments' });
  const [tournaments, setTournaments] = useState<MatchplayTournament[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load tournaments on mount
  useEffect(() => {
    loadTournaments();
  }, []);

  const loadTournaments = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const tournamentList = await MatchplayDatabase.getTournaments();
      setTournaments(tournamentList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tournaments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTournament = () => {
    setViewState({ view: 'setup' });
  };

  const handleTournamentCreated = (tournamentId: string) => {
    setViewState({ view: 'leaderboard', tournamentId });
    loadTournaments(); // Refresh tournament list
  };

  const handleTournamentSelect = (tournamentId: string) => {
    setViewState({ view: 'leaderboard', tournamentId });
  };

  const handleMatchSelect = (matchId: string, tournamentId: string) => {
    setViewState({ view: 'scoring', matchId, tournamentId });
  };

  const handleMatchComplete = (matchId: string) => {
    // Return to leaderboard when match is completed
    if (viewState.tournamentId) {
      setViewState({ view: 'leaderboard', tournamentId: viewState.tournamentId });
    }
  };

  const handleBackToTournaments = () => {
    setViewState({ view: 'tournaments' });
  };

  const handleBackToLeaderboard = () => {
    if (viewState.tournamentId) {
      setViewState({ view: 'leaderboard', tournamentId: viewState.tournamentId });
    } else {
      setViewState({ view: 'tournaments' });
    }
  };

  const getCurrentTournament = () => {
    if (!viewState.tournamentId) return null;
    return tournaments.find(t => t.id === viewState.tournamentId) || null;
  };

  const getHeaderTitle = () => {
    const tournament = getCurrentTournament();
    
    switch (viewState.view) {
      case 'tournaments':
        return 'Matchplay v2';
      case 'setup':
        return 'Create Tournament';
      case 'leaderboard':
        return tournament?.name || 'Tournament';
      case 'scoring':
        return tournament ? `${tournament.name} - Scoring` : 'Match Scoring';
      default:
        return 'Matchplay v2';
    }
  };

  const getHeaderSubtitle = () => {
    const tournament = getCurrentTournament();
    
    switch (viewState.view) {
      case 'tournaments':
        return 'Select or create a tournament';
      case 'setup':
        return 'Configure tournament settings';
      case 'leaderboard':
        return tournament ? `${tournament.format.charAt(0).toUpperCase() + tournament.format.slice(1)} format` : '';
      case 'scoring':
        return tournament ? `${tournament.format.charAt(0).toUpperCase() + tournament.format.slice(1)} format` : '';
      default:
        return '';
    }
  };

  const showBackButton = viewState.view !== 'tournaments';

  const renderCurrentView = () => {
    switch (viewState.view) {
      case 'tournaments':
        return (
          <TournamentList
            tournaments={tournaments}
            onTournamentSelect={handleTournamentSelect}
            onCreateTournament={handleCreateTournament}
            isLoading={isLoading}
            error={error}
          />
        );

      case 'setup':
        return (
          <MatchplayTournamentSetup
            onTournamentCreated={handleTournamentCreated}
            onCancel={handleBackToTournaments}
          />
        );

      case 'leaderboard':
        return viewState.tournamentId ? (
          <MatchplayLeaderboard
            tournamentId={viewState.tournamentId}
            onMatchSelect={(matchId) => handleMatchSelect(matchId, viewState.tournamentId!)}
            onBack={handleBackToTournaments}
          />
        ) : null;

      case 'scoring':
        return viewState.matchId && viewState.tournamentId ? (
          <ScoringWrapper
            matchId={viewState.matchId}
            tournamentId={viewState.tournamentId}
            onBack={handleBackToLeaderboard}
          />
        ) : null;

      default:
        return <div>Unknown view</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Mobile Optimized */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
              {showBackButton && (
                <button
                  onClick={viewState.view === 'scoring' ? handleBackToLeaderboard : handleBackToTournaments}
                  className="flex items-center justify-center w-10 h-10 sm:w-8 sm:h-8 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 touch-manipulation active:scale-95 transition-all"
                >
                  <svg className="w-6 h-6 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                  {getHeaderTitle()}
                </h1>
                {getHeaderSubtitle() && (
                  <p className="text-sm text-gray-600 truncate">
                    {getHeaderSubtitle()}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {viewState.view === 'tournaments' && (
                <>
                  <button
                    onClick={loadTournaments}
                    disabled={isLoading}
                    className="flex items-center justify-center w-10 h-10 sm:w-9 sm:h-9 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 touch-manipulation active:scale-95 transition-all disabled:opacity-50"
                  >
                    <RefreshCw className={`w-5 h-5 sm:w-4 sm:h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  </button>
                  
                  <button
                    onClick={handleCreateTournament}
                    className="flex items-center gap-2 px-4 py-2 sm:px-3 sm:py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-sm touch-manipulation active:scale-95 transition-all"
                  >
                    <Plus className="w-5 h-5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">New Tournament</span>
                    <span className="sm:hidden">New</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {renderCurrentView()}
      </div>
    </div>
  );
}

// Wrapper component to handle data fetching for scoring
function ScoringWrapper({ matchId, tournamentId, onBack }: {
  matchId: string;
  tournamentId: string;
  onBack: () => void;
}) {
  const [match, setMatch] = useState<MatchplayMatch | null>(null);
  const [tournament, setTournament] = useState<MatchplayTournament | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [matchData, tournamentData] = await Promise.all([
          MatchplayDatabase.getMatch(matchId),
          MatchplayDatabase.getTournament(tournamentId)
        ]);
        
        setMatch(matchData);
        setTournament(tournamentData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load match data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [matchId, tournamentId]);

  if (isLoading) {
    return (
      <div className="w-full min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading match...</p>
        </div>
      </div>
    );
  }

  if (error || !match || !tournament) {
    return (
      <div className="w-full min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Failed to load match'}</p>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Back to Tournament
          </button>
        </div>
      </div>
    );
  }

  return (
    <MatchplayScoring
      match={match}
      tournament={tournament}
      onBack={onBack}
    />
  );
} 